import { AnimationBuilder, AnimationFactory, animate, group, keyframes, query, style } from '@angular/animations';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';

import { WINDOW } from '@frontend/vanilla/core';

@Component({
    selector: 'ms-stats-value',
    template: `
        <div #statsValue>
            <div #newValue style="display: none" class="newValue"></div>
            <div #oldValue style="display: none" class="oldValue"></div>
            <div #currentValue>{{ value }}</div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class StatsValueComponent implements OnInit, OnChanges, OnDestroy {
    @Input() value: number | string;
    private animationFactory: AnimationFactory | null = null;
    private animationTimer = 0;
    @ViewChild('newValue', { static: true }) newValueElement: ElementRef;
    @ViewChild('oldValue', { static: true }) oldValueElement: ElementRef;

    @ViewChild('currentValue', { static: true }) valueElement: ElementRef;
    @ViewChild('statsValue', { static: true }) statsElement: ElementRef;

    private readonly _window = inject(WINDOW);

    constructor(private animationBuilder: AnimationBuilder) {}

    ngOnInit(): void {
        this.animationFactory = this.animationBuilder.build(
            group([
                query(
                    '.newValue',
                    animate(
                        500,
                        keyframes([
                            style({ opacity: 0.3, transform: 'translateY(-15%)' }),
                            style({ opacity: 0.7, transform: 'translateY(-10%)' }),
                            style({ opacity: 1, transform: 'translateY(-8%)' }),
                            style({ opacity: 1, transform: 'translateY(-4%)' }),
                            style({ opacity: 1, transform: 'translateY(0%)' }),
                        ]),
                    ),
                ),
                query(
                    '.oldValue',
                    animate(
                        500,
                        keyframes([
                            style({ opacity: 1, transform: 'translateY(-45%)' }),
                            style({ opacity: 0.7, transform: 'translateY(-40%)' }),
                            style({ opacity: 0.3, transform: 'translateY(-35%)' }),
                            style({ opacity: 0, transform: 'translateY(-30%)' }),
                        ]),
                    ),
                ),
            ]),
        );
    }

    private doAnimation(previousValue: number, currentValue: number): void {
        if (previousValue !== currentValue && this.animationFactory) {
            this.animationTimer = this._window.setTimeout(() => {
                // No matter what happens in 500ms hide the elements.
                this.valueElement.nativeElement.style.display = 'block';
                this.newValueElement.nativeElement.style.display = 'none';
                this.oldValueElement.nativeElement.style.display = 'none';
            }, 500);

            try {
                this.valueElement.nativeElement.style.display = 'none';
                this.oldValueElement.nativeElement.style.display = 'block';
                this.newValueElement.nativeElement.style.display = 'block';

                this.oldValueElement.nativeElement.textContent = previousValue;
                this.newValueElement.nativeElement.textContent = currentValue;

                const player = this.animationFactory.create(this.statsElement.nativeElement);
                player.play();
            } catch {}
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('value' in changes && !changes.value.isFirstChange()) {
            this.doAnimation(changes.value.previousValue, changes.value.currentValue);
        }
    }

    ngOnDestroy(): void {
        if (this.animationTimer) {
            clearTimeout(this.animationTimer);
            this.animationTimer = 0;
        }
    }
}
